import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Card, Col, Row, Typography, notification } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import SalesOrderStore from '@/stores/SalesOrderStore';
import ContactStore from '@/stores/ContactStore';
import QuoteStore from '@/stores/QuoteStore';
import UIStore from '@/stores/UIStore';

import PortalLoading from '@/components/PortalLoading';
import { PageHeader } from '@/components/PageHeader';
import { ErrorRender } from '@partsbadger/library';
import type { QuoteProps } from '@partsbadger/types';

import OrderItemsReview from '../OrderReview/OrderItemsReview';
import OrderCheckoutSummary, { OrderCheckoutFormData } from '../OrderReview/OrderCheckoutSummary';
import { AdditionalRequirementsSummaryCard } from '../OrderReview/AdditionalRequirementsSummaryCard';

const { Title } = Typography;

interface Params {
    id: string; // reorder route param
}

type SubmitOverrides = {
    phone?: string;
    email?: string;
    shippingAccountNumber?: string;
    carrier?: string;
    shippingMethod?: string;
    saveShippingDefaults?: boolean;

    // reorder-only
    purchaseOrderInfo?: { poFiles?: any[] };
    notes?: string;
};

type ReorderLinkedQuote = {
    id?: number | string;
};

type ReorderOrderShape = {
    quotes?: Array<number | string | ReorderLinkedQuote>;
    additional_requirements?: string[];
};

type ReorderLineItemShape = {
    quote_has_product?: {
        quote_related?: ReorderLinkedQuote;
    };
};

type AdditionalRequirementsQuote = QuoteProps & {
    application_cost_items?: unknown[];
};

const toPositiveNumber = (value: unknown) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
};

const extractUploadedFileIds = (poFiles: any[]) => {
    return (poFiles ?? [])
        .filter(f => f?.status === 'done')
        .map(f => f?.response?.id ?? f?.response?.file_id ?? f?.response?.pk ?? f?.id)
        .filter(Boolean);
};
const buildReorderPayload = (
    order: any,
    items: any[],
    overrides: SubmitOverrides | undefined,
    opts: { payWithCreditCard: boolean; creditCardToken?: string }
) => {
    const poFiles = overrides?.purchaseOrderInfo?.poFiles ?? [];
    const po_file_ids = extractUploadedFileIds(poFiles);

    const base: Record<string, unknown> = {
        billing_name: order?.billing_name,
        billing_street: order?.billing_street,
        billing_city: order?.billing_city,
        billing_country: order?.billing_country,
        billing_state: order?.billing_state,
        billing_zip_code: order?.billing_zip_code,

        shipping_name: order?.shipping_name,
        shipping_street: order?.shipping_street,
        shipping_city: order?.shipping_city,
        shipping_country: order?.shipping_country,
        shipping_state: order?.shipping_state,
        shipping_zip_code: order?.shipping_zip_code,

        pay_with_credit_card: opts.payWithCreditCard,
        po_files: po_file_ids,
        notes: overrides?.notes ?? '',

        line_items: (items ?? []).map((it: any) => ({
            id: it.id,
            quantity: it.quantity,
        })),
    };

    if (opts.payWithCreditCard && opts.creditCardToken) {
        base.credit_card_token = opts.creditCardToken;
    }

    return base;
};

const ReorderV2: React.FC = observer(() => {
    const history = useHistory();
    const { id } = useParams<Params>();

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [quoteForReqs, setQuoteForReqs] = useState<QuoteProps | null>(null);

    const [formData, setFormData] = useState<OrderCheckoutFormData>({
        shippingAddressId: undefined,
        billingAddressId: undefined,
        phone: '',
        email: '',
        billingSameAsShipping: true,
        paymentMethod: 'credit_card',
        purchaseOrderInfo: { poFiles: [] },
        saveShippingDefaults: false,
    });

    const onFormUpdate = <K extends keyof OrderCheckoutFormData>(key: K, value: OrderCheckoutFormData[K]) =>
        setFormData(prev => ({ ...prev, [key]: value }));

    useEffect(() => {
        (async () => {
            try {
                UIStore.setDocumentTitle('Reordering');
                setLoading(true);

                await SalesOrderStore.getOrderById(Number(id));

                const contactId = SalesOrderStore.order?.contact?.id;
                if (contactId) {
                    await ContactStore.getContactById(contactId);
                    await ContactStore.getAdditionalRequirementsAndDocuments(contactId);
                }

                const c = ContactStore.contact;
                const o: any = SalesOrderStore.order;

                setFormData(prev => ({
                    ...prev,
                    phone: prev.phone || String(c?.phone ?? o?.contact?.phone ?? o?.phone ?? '').trim(),
                    email: prev.email || String(c?.email ?? o?.contact?.email ?? o?.email ?? '').trim(),
                }));
            } catch (err: any) {
                notification.error({ message: 'Error loading reorder', description: <ErrorRender error={err} /> });
                history.replace('/my-account/orders/');
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const derivedQuoteId = useMemo(() => {
        const order = (SalesOrderStore.order as unknown) as ReorderOrderShape | undefined;
        const orderQuotes = order?.quotes ?? [];
        const firstQuote = orderQuotes[0];
        const linkedQuoteId = toPositiveNumber(
            typeof firstQuote === 'object' && firstQuote !== null ? firstQuote.id : firstQuote
        );

        if (linkedQuoteId) {
            return linkedQuoteId;
        }

        const orderItems = (SalesOrderStore.order_items ?? []) as ReorderLineItemShape[];
        const rawLineItemQuoteId = orderItems.find(item => item?.quote_has_product?.quote_related?.id != null)
            ?.quote_has_product?.quote_related?.id;

        return toPositiveNumber(rawLineItemQuoteId);
    }, [SalesOrderStore.order?.quotes, SalesOrderStore.order_items]);

    useEffect(() => {
        if (!derivedQuoteId) {
            setQuoteForReqs(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const q = await QuoteStore.getQuoteById(derivedQuoteId);
                if (!cancelled) setQuoteForReqs(q);
            } catch (e) {
                if (!cancelled) setQuoteForReqs(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [derivedQuoteId]);

    const requirementsQuote = quoteForReqs as AdditionalRequirementsQuote | null;
    const customReqs = requirementsQuote?.custom_requirements ?? [];
    const appCostItems = requirementsQuote?.application_cost_items ?? [];
    const orderAdditionalRequirements = (((SalesOrderStore.order as unknown) as ReorderOrderShape | undefined)
        ?.additional_requirements ?? []) as string[];
    const hasAdditionalRequirements =
        customReqs.length > 0 || appCostItems.length > 0 || orderAdditionalRequirements.length > 0;

    const handleCardToken = async (token: string, overrides?: SubmitOverrides) => {
        try {
            setSubmitting(true);

            const order = SalesOrderStore.order as any;
            const items = SalesOrderStore.order_items as any[];

            const merged: SubmitOverrides = {
                ...overrides,
                purchaseOrderInfo: formData.purchaseOrderInfo,
                notes: formData.notes,
            };

            const payload = buildReorderPayload(order, items, merged, {
                payWithCreditCard: true,
                creditCardToken: token,
            });

            await SalesOrderStore.reOrder(Number(id), payload);

            const ccPayload = {
                amount: String(order.grand_total ?? order.total_cost ?? ''),
                customer_email: ContactStore.contact?.email ?? '',
                representative_email: (ContactStore.contact as any)?.account_owner?.email ?? '',
                notes: merged.notes ?? '',
                quotes: SalesOrderStore.quotes,
                ship_cost: '0',
                tax_cost: '0',
                zip_code: String(order.billing_zip_code ?? ''),
            };
            await SalesOrderStore.sendCreditCard(ccPayload);

            history.push({
                pathname: '/my-account/orders/reorder/success/',
                state: {
                    status: 'success',
                    title: 'Thank you for submitting your Purchase Order!',
                    subTitle: 'Your Order will be available in your Orders tab soon',
                },
            });
        } catch (err: any) {
            notification.error({ message: 'Reorder submit failed', description: <ErrorRender error={err} /> });
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmitPO = async (_tokenOverride?: string, overrides?: SubmitOverrides) => {
        try {
            setSubmitting(true);

            const order = SalesOrderStore.order as any;
            const items = SalesOrderStore.order_items as any[];

            const merged: SubmitOverrides = {
                ...overrides,
                purchaseOrderInfo: formData.purchaseOrderInfo,
                notes: formData.notes,
            };

            const payload = buildReorderPayload(order, items, merged, { payWithCreditCard: false });

            await SalesOrderStore.reOrder(Number(id), payload);

            history.push({
                pathname: '/my-account/orders/reorder/success/',
                state: {
                    status: 'success',
                    title: 'Thank you for submitting your Purchase Order!',
                    subTitle: 'Your Order will be available in your Orders tab soon',
                },
            });
        } catch (err: any) {
            notification.error({ message: 'Reorder submit failed', description: <ErrorRender error={err} /> });
        } finally {
            setSubmitting(false);
        }
    };

    const noOp = async () => {};

    if (loading || !SalesOrderStore.order || !ContactStore.contact) return <PortalLoading />;

    return (
        <>
            <PageHeader>
                <div className="d-flex flex-column align-items-baseline">
                    <Button
                        type="link"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => history.goBack()}
                        className="text-primary m-0 p-0"
                    >
                        Back
                    </Button>

                    <Title level={1} className="mb-0">
                        Reorder - #{SalesOrderStore.order?.name}
                    </Title>
                </div>
            </PageHeader>

            <div className="container" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Row gutter={[16, 16]} style={{ flex: 1, minHeight: 0 }}>
                    <Col
                        xl={16}
                        lg={16}
                        md={24}
                        span={24}
                        style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
                    >
                        <div
                            style={{
                                flex: 1,
                                minHeight: 0,
                                overflowY: UIStore.isConstrainedViewport ? 'visible' : 'auto',
                            }}
                        >
                            <OrderItemsReview
                                order={SalesOrderStore.order}
                                order_items={SalesOrderStore.order_items}
                                contact={ContactStore.contact}
                                onEditOrder={() => {
                                    notification.info({
                                        message: 'Edit not available',
                                        description: 'Edit flow not enabled for reorder.',
                                    });
                                }}
                                quote={quoteForReqs ?? undefined}
                                mode="reorder"
                            />

                            {hasAdditionalRequirements && (
                                <Card className="mt-3">
                                    <AdditionalRequirementsSummaryCard
                                        quote={quoteForReqs}
                                        mode={'quote_detail' as any}
                                        isReorder
                                        salesOrderRequirements={orderAdditionalRequirements}
                                    />
                                </Card>
                            )}
                        </div>
                    </Col>

                    <Col xl={8} lg={8} md={24} span={24}>
                        <OrderCheckoutSummary
                            order={SalesOrderStore.order}
                            contact={ContactStore.contact}
                            order_items={SalesOrderStore.order_items}
                            formData={formData}
                            submitting={submitting}
                            onFormUpdate={onFormUpdate}
                            onSubmitPO={handleSubmitPO}
                            onCreditCardToken={handleCardToken}
                            onShippingAddressChange={noOp as any}
                            onBillingAddressChange={noOp as any}
                            onBillingSameAsShippingChange={noOp as any}
                            mode="reorder"
                        />
                    </Col>
                </Row>
            </div>
        </>
    );
});

export default ReorderV2;
